import React, { useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet, Link, useNavigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Toaster } from 'react-hot-toast';
import { onAuthStateChanged } from 'firebase/auth';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import { auth, db } from './firebase';
import { logInfraction } from './utils/security';
import { toast } from 'react-hot-toast';

// ── Referral Engine ───────────────────────────────────────────────────────────
// getReferralIdFromUrl: reads ?ref= on mount before SPA routing removes it
// handleReferralLink:   writes referrerId to new user's Firestore doc
// triggerReferrerReward: fires the reward (+1 bonusScan / commission reduction)
import {
  getReferralIdFromUrl,
  handleReferralLink,
  triggerReferrerReward,
} from './utils/referral';

// Components
import Sidebar from './components/sidebar';
import Login from './components/login';
import Home from './components/home';
import Scan from './components/scan';
import Styles from './components/styles';
import Checkout from './components/checkout';
import Profile from './components/profile';
import UserDashboard from './components/userdashboard';
import BookingHistory from './components/bookinghistory';
import StylistDashboard from './components/stylistDashboard';
import StylistFinances from './components/stylistfinances';
import SignatureStylesUpload from './components/signaturestylesupload';
import PortfolioUpload from './components/portfolioupload';
import Barbers from './components/barbers';
import BarberProfile from './components/barberprofile';
import ImportStyle from './components/importstyle';
import StyleSnap from './components/stylesnap';
import StyleViewer from './components/styleviewer';
import ViewerErrorBoundary from './components/ErrorBoundary';
import AiRender from './components/AiRender';
import Help from './components/help';
import Wallet from './components/wallet';
import AdminDashboard from './components/admindashboard';
import AdminPanel from './components/adminpanel';
import BroadcastTool from './components/broadcast-tool';
import BarberDetail from './components/barber-detail';
import GlobalGallery from './components/global-gallery';
import Store from './components/store';
import ClientGallery from './components/clientgallery';
import Workstation from './components/workstation';
import DiscoveryEngine from './components/discovery-engine';
import AssetInventory from './components/asset-inventory';
import AssetStore from './components/asset-store';
import BookingCompletionUI from './components/bookingcompletionUI';
import AdminGlobalBroadcast from './components/adminglobalbroadcast';
import Inbox from './components/inbox';
import Maintenance from './components/maintenance';
import BookingSuccess from './components/bookingsuccess';
import PersonalLookbook from './components/PersonalLookbook';
import BarberView from './components/BarberView';
import BiometricUnlock from './screens/BiometricUnlock';
import StyleLibrary from './components/StyleLibrary';
import StylistOnboarding from './components/StylistOnboarding';
import ExternalStyleViewer from './components/ExternalStyleViewer';
import Payment from './components/payment';
import SubscriptionPage from './components/SubscriptionPage';
import AdminSupportTab from './components/AdminSupportTab';
import AdminVaultManager from './components/AdminVaultManager';

// --- BIOMETRIC & 3D COMPONENTS ---
import BiometricService from './components/biometricService';
import ThreeViewer from './components/ThreeViewer';
import ShareCodeViewer from './components/ShareCodeViewer';
import StylistConsultationView from './components/StylistConsultationView';
import VerifyEmail from './components/VerifyEmail';
import WaitlistConfirmed from './components/WaitlistConfirmed';

// Style Try-On Component (exact filename: StyleTryon.jsx)
import StyleTryOn from './components/StyleTryon';

import MerchantPortal from './components/merchant/MerchantPortal';
import MerchantMarketplaceCarousel from './components/merchant/MerchantMarketplaceCarousel';
import MerchantProductPage from './components/merchant/MerchantProductPage';
import Merchant_dashboard from './components/merchant/Merchant_dashboard';
import Merchant_wallet from './components/merchant/Merchant_wallet';
import Merchant_checkout from './components/merchant/Merchant_checkout';
import Merchant_payment from './components/merchant/Merchant_payment';
import Merchant_adminpanel from './components/merchant/Merchant_adminpanel';

import ResetPassword from './components/ResetPassword';

const theme = {
  colors: {
    gold:  '#B8860B',
    navy:  '#001F3F',
    black: '#000000',
  },
};

const SUPER_ADMINS = ['FFXDokpj5IXR3SFSvJyDu70LRp73', 'GbjKMdeVEmTgMefcU1cEHsEOstn1'];

const stripePromise = loadStripe('pk_test_51SaFW5AfXtDdFCGlAboHiWv9DbJiwZSmdfPPioyOGeQ2Xk9tYFMHcUH5uGjeVZgsIAWxcDAqADHxH19kM2Ahlaif007h5rZQmy');

const queryClient = new QueryClient();

// ─────────────────────────────────────────────────────────────
// AUTH & ROUTE GUARDS
// ─────────────────────────────────────────────────────────────
function AdminRoute({ children }) {
  const [hasAccess, setHasAccess] = useState(false);
  const [checking,  setChecking]  = useState(true);

  useEffect(() => {
    async function checkRole() {
      const u = auth.currentUser;
      if (u) {
        if (SUPER_ADMINS.includes(u.uid)) {
          setHasAccess(true);
        } else {
          const uSnap = await getDoc(doc(db, 'users', u.uid)).catch(() => ({ exists: () => false }));
          const data  = uSnap.exists() ? uSnap.data() : null;
          if (data?.role === 'admin' || data?.role === 'finance') setHasAccess(true);
        }
      }
      setChecking(false);
    }
    checkRole();
  }, []);

  if (checking) return <LoadingScreen />;
  return hasAccess ? children : <Navigate to="/dashboard" replace />;
}

/**
 * ProtectedLayout: The Core Security Guard
 * Handles: Authentication, Email Verification, Maintenance Mode, and Pre-reg Approval Status.
 */
function ProtectedLayout() {
  const [user,              setUser]              = useState(null);
  const [userData,          setUserData]          = useState(null);
  const [loading,           setLoading]           = useState(true);
  const [isMaintenanceActive, setIsMaintenanceActive] = useState(false);
  const [isMobile,          setIsMobile]          = useState(window.innerWidth < 1024);
  const navigate = useNavigate();

  // Unified Effect for Auth, Maintenance, and Security
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 1024);
    window.addEventListener('resize', handleResize);

    // 1. Listen for Maintenance Mode
    const unsubLock = onSnapshot(doc(db, 'system', 'maintenance'), (snap) => {
      if (snap.exists()) setIsMaintenanceActive(snap.data().active);
    });

    // 2. Listen for Auth Changes
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // Check waitlist first (pending status)
        const waitlistSnap = await getDoc(doc(db, 'waitlist', u.uid));
        if (waitlistSnap.exists() && waitlistSnap.data().status === 'pending') {
          setUserData({ status: 'pending' });
          setUser(u);
          setLoading(false);
          return;
        }

        // Then check users or barbers
        const uSnap = await getDoc(doc(db, 'users', u.uid));
        const bSnap = await getDoc(doc(db, 'barbers', u.uid));
        const data  = uSnap.exists() ? uSnap.data() : (bSnap.exists() ? bSnap.data() : null);

        setUserData(data);
        setUser(u);
      } else {
        setUser(null);
        setUserData(null);
      }
      setLoading(false);
    });

    return () => {
      unsubAuth();
      unsubLock();
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // 3. Independent Security Guard Effect
  useEffect(() => {
    if (!loading && user) {
      const isElevated = SUPER_ADMINS.includes(user.uid) || userData?.role === 'admin';

      if (!isElevated && window.location.pathname.startsWith('/admin')) {
        logInfraction(
          'UNAUTHORIZED_ROUTE_ACCESS',
          `User ${user.email} attempted to force-entry into the Admin Panel.`
        );
        navigate('/dashboard');
      }
    }
  }, [user, userData, loading, navigate]);

  if (loading) return <LoadingScreen />;

  // 1. Not logged in → login
  if (!user) return <Navigate to="/login" replace />;

  const isElevated = SUPER_ADMINS.includes(user.uid) || userData?.role === 'admin';

  // 2. Pending → back to home
  if (userData?.status === 'pending' && !isElevated) {
    return <Navigate to="/" replace />;
  }

  // 3. Email not verified → verify page
  if (!user.emailVerified && !isElevated) {
    return <Navigate to="/verify-email" replace />;
  }

  // 4. Maintenance → block non-admins
  if (isMaintenanceActive && !isElevated) {
    return <Navigate to="/maintenance" replace />;
  }

  return (
    <div style={{ display: 'flex', background: theme.colors.black, overflowX: 'hidden' }}>
      <Sidebar />
      <main style={{
        flex:       1,
        marginLeft: isMobile ? '0' : '260px',
        minHeight:  '100vh',
        width:      '100%',
        transition: 'margin-left 0.3s ease',
        position:   'relative',
        maxWidth:   isMobile ? '100vw' : 'calc(100vw - 260px)',
      }}>
        <Outlet />
      </main>
    </div>
  );
}

/**
 * MerchantProtectedLayout: Specific guard for Merchant Dashboard
 */
function MerchantProtectedLayout() {
  const [user,    setUser]    = useState(null);
  const [merchant, setMerchant] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (u) => {
      if (!u) {
        navigate('/');
        setLoading(false);
        return;
      }

      // Super admins always get access
      if (SUPER_ADMINS.includes(u.uid)) {
        setUser(u);
        setLoading(false);
        return;
      }

      // Check merchant status
      const mSnap = await getDoc(doc(db, 'merchants', u.uid)).catch(() => null);
      if (!mSnap?.exists()) {
        navigate('/');
        setLoading(false);
        return;
      }

      const mData = mSnap.data();
      if (mData.status !== 'approved') {
        navigate('/');
        setLoading(false);
        return;
      }

      setUser(u);
      setMerchant(mData);
      setLoading(false);
    });

    return () => unsub();
  }, [navigate]);

  if (loading) return <LoadingScreen />;
  if (!user)   return <Navigate to="/" replace />;

  return <Outlet />;
}

// ─────────────────────────────────────────────────────────────
// UI HELPERS
// ─────────────────────────────────────────────────────────────
const NotFound = () => (
  <div style={premiumLoaderContainer}>
    <h1 style={{ color: theme.colors.gold, fontSize: '5rem', margin: 0, fontWeight: '900' }}>404</h1>
    <p style={{ color: '#FFF', letterSpacing: '4px', marginBottom: '30px', fontWeight: 'bold' }}>LOST IN THE VAULT</p>
    <Link to="/" style={{
      background:    theme.colors.gold,
      color:         '#000',
      padding:       '14px 40px',
      borderRadius:  '50px',
      textDecoration:'none',
      fontWeight:    '900',
      boxShadow:     '0 4px 15px rgba(184, 134, 11, 0.3)',
      fontSize:      '0.8rem',
      letterSpacing: '1px',
    }}>
      RETURN TO HQ
    </Link>
  </div>
);

const LoadingScreen = () => (
  <div style={premiumLoaderContainer}>
    <div style={pulsingLogoContainer}>
      <div style={spinnerStyle} />
      <p style={{ color: theme.colors.gold, marginTop: '20px', letterSpacing: '3px', fontWeight: 'bold' }}>ESDRAS</p>
    </div>
  </div>
);

const getHardwareId = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl     = canvas.getContext('webgl');
    if (!gl) return 'WEB_FALLBACK_ID';
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    const renderer  = debugInfo ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) : 'UNKNOWN';
    return btoa(`${navigator.userAgent}-${renderer}-${screen.width}x${screen.height}`);
  } catch (e) {
    return 'WEB_FALLBACK_ID';
  }
};

// ─────────────────────────────────────────────────────────────
// MAIN APP COMPONENT
// ─────────────────────────────────────────────────────────────
export default function App() {
  const [user,          setUser]          = useState(null);
  const [role,          setRole]          = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [isMaintenance, setIsMaintenance] = useState(false);

  useEffect(() => {
    // ── REFERRAL: Capture ?ref= before SPA routing removes it ────────────────
    // Must run before any navigation. Stores the referrer UID in sessionStorage
    // so it survives page transitions inside the SPA.
    // handleReferralLink() + triggerReferrerReward() are called further below
    // inside onAuthStateChanged, AFTER we confirm the user is newly signed up.
    const pendingRefFromUrl = getReferralIdFromUrl();
    if (pendingRefFromUrl) {
      sessionStorage.setItem('pendingReferral', pendingRefFromUrl);
      // Clean the URL immediately so the UID isn't visible in the address bar
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    // ── Inject Monnify for Nigeria Payments ───────────────────────────────────
    const script = document.createElement('script');
    script.src   = 'https://sdk.monnify.com/plugin/monnify.js';
    script.async = true;
    document.head.appendChild(script);

    // ── Maintenance Mode Listener ─────────────────────────────────────────────
    const unsubLock = onSnapshot(doc(db, 'system', 'maintenance'), (snap) => {
      if (snap.exists()) setIsMaintenance(snap.data().active);
    });

    // ── Auth State Listener ───────────────────────────────────────────────────
    const unsubAuth = onAuthStateChanged(auth, async (u) => {
      if (u) {
        // MERGED CHECK: Verify waitlist first to ensure global 'role' matches ProtectedLayout logic
        const wSnap = await getDoc(doc(db, 'waitlist', u.uid)).catch(() => ({ exists: () => false }));

        if (wSnap.exists() && wSnap.data().status === 'pending') {
          setRole('pending');
        } else {
          const uSnap = await getDoc(doc(db, 'users',   u.uid)).catch(() => ({ exists: () => false }));
          const bSnap = await getDoc(doc(db, 'barbers', u.uid)).catch(() => ({ exists: () => false }));
          const data  = uSnap.exists() ? uSnap.data() : (bSnap.exists() ? bSnap.data() : null);

          // Determine final role for state
          setRole(data?.role || (bSnap.exists() ? 'stylist' : 'user'));

          // ── REFERRAL: Fire reward for newly signed-up users ─────────────────
          // Conditions:
          //   1. There's a pending referral stored from the signup URL
          //   2. The user has NOT already received a referral reward
          //   3. The user exists (not a ghost auth record)
          // This fires on every auth state change but is idempotent:
          //   - referralRewardIssued flag prevents double-rewarding
          //   - sessionStorage.removeItem ensures it only runs once per session
          const storedRef = sessionStorage.getItem('pendingReferral');
          if (storedRef && data && !data.referralRewardIssued) {
            try {
              // Step 1: attach the referrerId to the new user's doc (if not already set)
              await handleReferralLink(u.uid, storedRef);
              // Step 2: issue the reward to the referrer (bonusScan or commission reduction)
              await triggerReferrerReward(u.uid);
            } catch (refErr) {
              console.warn('[Referral] Reward flow error (non-critical):', refErr.message);
            } finally {
              // Always clear sessionStorage so this never re-fires for this session
              sessionStorage.removeItem('pendingReferral');
            }
          }
        }
      } else {
        setRole(null);
      }

      setUser(u);
      setLoading(false);
    });

    return () => {
      unsubAuth();
      unsubLock();
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  if (loading) return <LoadingScreen />;

  const isElevated = user && (SUPER_ADMINS.includes(user.uid) || role === 'admin');

  return (
    <QueryClientProvider client={queryClient}>
      <Elements stripe={stripePromise}>
        <BrowserRouter>
          <Toaster position="top-center" reverseOrder={false} />
          <Routes>
            {/* PUBLIC ROUTES */}
            <Route path="/"          element={isMaintenance && !isElevated ? <Maintenance /> : <Home user={user} />} />
            <Route path="/register"  element={<Home user={user} />} />
            <Route path="/login"     element={<Login />} />
            <Route path="/reset-password"    element={<ResetPassword />} />
            <Route path="/maintenance"       element={<Maintenance />} />
            <Route path="/verify-email"      element={<VerifyEmail />} />
            <Route path="/waitlist-confirmed" element={<WaitlistConfirmed />} />
            <Route path="/barber-view"       element={<BarberView />} />
            <Route path="/barber-view/:code" element={<BarberView />} />
            <Route path="/view-style/:code"  element={<ExternalStyleViewer />} />
            <Route path="/shared-access/:shareCode" element={<ShareCodeViewer />} />

            {/* PUBLIC MERCHANT ROUTES — No auth wall for browsing */}
            <Route path="/marketplace/product/:productId" element={<MerchantProductPage />} />
            <Route path="/merchant/checkout" element={<Merchant_checkout />} />
            <Route path="/merchant/payment"  element={<Merchant_payment />} />

            <Route
              path="/unlock"
              element={user ? <BiometricUnlock deviceId={getHardwareId()} /> : <Navigate to="/login" replace />}
            />

            {/* PROTECTED APP SHELL */}
            <Route element={<ProtectedLayout />}>
              <Route path="/dashboard"            element={<UserDashboard />} />
              <Route path="/inbox"                element={<Inbox />} />
              <Route path="/bookings"             element={<BookingHistory />} />
              <Route path="/consultation-history" element={<BookingHistory />} />
              <Route path="/profile"              element={<Profile />} />
              <Route path="/help"                 element={<Help />} />
              <Route path="/wallet"               element={<Wallet />} />
              <Route path="/lookbook"             element={<PersonalLookbook />} />
              <Route path="/style-library"        element={<StyleLibrary />} />

              <Route path="/scan"             element={<Scan />} />
              <Route path="/biometric-health" element={<BiometricService />} />

              <Route path="/styles"    element={<Styles />} />
              <Route path="/ai-engine" element={<AiRender />} />
              <Route path="/stylesnap" element={<StyleSnap />} />
              {/* Style Try-On Route - matches the actual filename StyleTryon.jsx */}
              <Route path="/style-tryon" element={<StyleTryOn />} />

              <Route path="/viewer"                          element={<ViewerErrorBoundary><StyleViewer /></ViewerErrorBoundary>} />
              <Route path="/viewer/:styleId"                 element={<ThreeViewer />} />
              <Route path="/style-static-preview/:styleId"   element={<ViewerErrorBoundary><StyleViewer /></ViewerErrorBoundary>} />

              {/* Subscription & Payment Routes */}
              <Route path="/subscription"  element={<SubscriptionPage />} />
              <Route path="/checkout"      element={<Checkout />} />
              <Route path="/discovery"     element={<DiscoveryEngine />} />
              <Route path="/barbers"       element={<Barbers />} />
              <Route path="/barber/:id"    element={<BarberProfile />} />
              <Route path="/portfolio/:barberId"   element={<ClientGallery />} />
              <Route path="/barber-detail/:barberId" element={<BarberDetail />} />
              <Route path="/gallery" element={<GlobalGallery />} />
              <Route path="/store"   element={<Store />} />
              <Route path="/onboarding" element={<StylistOnboarding />} />

              <Route path="/stylist/dashboard"            element={<StylistDashboard />} />
              <Route path="/stylist/finances"             element={<StylistFinances />} />
              <Route path="/stylist/workstation"          element={<Workstation />} />
              <Route path="/stylist/broadcast"            element={<BroadcastTool />} />
              <Route path="/stylist/upload"               element={<SignatureStylesUpload />} />
              <Route path="/stylist/portfolio-upload"     element={<PortfolioUpload />} />
              <Route path="/stylist/consultation/:clientId" element={<StylistConsultationView />} />

              <Route path="/inventory"   element={<AssetInventory />} />
              <Route path="/asset-store" element={<AssetStore />} />
              <Route path="/importstyle" element={<ImportStyle />} />

              <Route path="/success"         element={<BookingCompletionUI />} />
              <Route path="/booking-success" element={<BookingSuccess />} />
              <Route path="/payment"         element={<Payment />} />

              {/* MERCHANT PROTECTED SHELL */}
              <Route element={<MerchantProtectedLayout />}>
                <Route path="/merchant/dashboard" element={<Merchant_dashboard />} />
                <Route path="/merchant/wallet"    element={<Merchant_wallet />} />
              </Route>

              {/* ADMIN SECURE ZONE */}
              <Route path="/admin"                element={<AdminRoute><AdminPanel /></AdminRoute>} />
              <Route path="/admin/dashboard"      element={<AdminRoute><AdminDashboard /></AdminRoute>} />
              <Route path="/admin/finance-ledger" element={<AdminRoute><AdminPanel /></AdminRoute>} />
              <Route path="/admin/support"        element={<AdminRoute><AdminSupportTab /></AdminRoute>} />
              <Route path="/admin/global-blast"   element={<AdminRoute><AdminGlobalBroadcast /></AdminRoute>} />
              <Route path="/admin/vault"          element={<AdminRoute><AdminVaultManager /></AdminRoute>} />
              <Route path="/admin/merchant"       element={<AdminRoute><Merchant_adminpanel /></AdminRoute>} />
            </Route>

            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </Elements>
    </QueryClientProvider>
  );
}

// ─────────────────────────────────────────────────────────────
// GLOBAL STYLES & MODAL STYLES
// ─────────────────────────────────────────────────────────────
const styleSheet = document.createElement('style');
styleSheet.innerText = `
  @keyframes spin { 100% { transform: rotate(360deg); } }
  body { margin: 0; font-family: 'Inter', sans-serif; background: #000; color: #fff; }
`;
document.head.appendChild(styleSheet);

const premiumLoaderContainer = {
  minHeight:   '100vh',
  background:  '#000',
  display:     'flex',
  flexDirection:'column',
  alignItems:  'center',
  justifyContent:'center',
  position:    'fixed',
  width:       '100%',
  top:         0,
  left:        0,
  zIndex:      9999,
};

const pulsingLogoContainer = {
  display:       'flex',
  flexDirection: 'column',
  alignItems:    'center',
};

const spinnerStyle = {
  width:       '50px',
  height:      '50px',
  border:      '3px solid rgba(184, 134, 11, 0.1)',
  borderTop:   '3px solid #B8860B',
  borderRadius:'50%',
  animation:   'spin 1s linear infinite',
};
